<template>
  <div class="pr-2 md:pr-10 pl-2">
    <div class="flex items-center pb-4">
      <div class="text-13 mr-auto font-600">{{ total }} users</div>
      <VDropdown ref="tooltip" class="inline" placement="bottom-end" :triggers="['click']" @show="onUserDropdownShown">
        <PillButton :text="$t('users.add_user')" icon="plus" primary />
        <template #popper>
          <div class="flex min-w-300 pt-4 px-4 items-center justify-between">
            <div class="text-13 font-600">Enter user's email address</div>
            <Icon name="clear" class="w-4 h-4 cursor-pointer" />
          </div>
          <div class="p-4">
            <ValidationObserver ref="validationObserver" v-slot="{ validate, errors }" class="w-full">
              <FormInput
                v-model="email"
                placeholder="Type an emаil address"
                :readonly="roleChanging"
                name="emаil"
                rules="required|email"
                :loading="emailChecking"
                icon="email"
                :error="errors.email"
                @update:modelValue="onEmailInput(validate)"
              />
              <FormLabel v-if="userAction === 'existing_project_user'" class="text-green-700" label="User is already added"></FormLabel>
            </ValidationObserver>
            <div class="flex items-center">
              <div v-if="userAction === 'existing_user'" class="flex mt-3 items-center w-full">
                <Avatar size="6" :picture="userPicture" class="mr-2" />
                <span class="text-13">{{ userName || email }}</span>
                <IconButton v-if="false" icon="plus" size="4" dense primary class="ml-auto" @click="onAddExistingUserClick"></IconButton>
                <PillButton primary text="Add" class="ml-auto" small :loading="roleChanging" :disabled="roleChanging" @click="onAddExistingUserClick"></PillButton>
              </div>
              <PillButton v-if="userAction === 'add_user'" text="Create user" primary class="mt-3 ml-auto" @click="onAddUserClick(email)"></PillButton>
            </div>
          </div>
        </template>
      </VDropdown>
    </div>
    <div>
      <div v-if="fetching">
        <div class="flex-grow flex items-center justify-center h-full">
          <Spinner class="w-8" />
        </div>
      </div>
      <div v-else-if="!users.length" class="w-full text-center text-13 text-gray-600">There is no record found!</div>
      <div v-else class="justify-items-center">
        <ListCard v-for="user in users" :key="user.email" class="mb-4 !p-1 sm:p-3 bg-white w-full">
          <div class="cursor-pointer w-6 h-6 flex-shrink-0 flex-grow-0 mr-3">
            <Avatar size="6" :picture="user.photoUrl" />
          </div>
          <div class="overflow-hidden">
            <div class="truncate">
              {{ user.email }} <span class="font-600">{{ user.name }}</span>
            </div>
            <div class="text-12">{{ $t('users.last_login') }} {{ user.last_login ? dayjs(user.last_login).format('MMM DD, YYYY HH:mm') : $t('n_a') }}</div>
          </div>
          <div class="ml-auto flex">
            <Badge v-if="user.disabled" warn small text="Disabled" class="mt-2 h-5 ml-1 sm:ml-3" />
            <Badge v-if="!user.disabled" success small text="Active" class="mt-2 h-5 ml-1 sm:ml-3" />
            <IconButton v-if="!user.disabled" class="text-primary" icon="person_cancel" title="Disable Access" @click="onDisableClick(user)" />
            <IconButton v-if="user.disabled" class="text-primary" icon="person_check" title="Enable Access" @click="onEnableClick(user)" />
            <IconButton title="Data Management" class="text-primary" icon="hub" @click="onDataManagementClick(user)" />
            <IconButton :title="$t('edit')" class="text-primary" icon="edit" @click="onEditClick(user)" />
            <IconButton class="text-primary" icon="delete" :title="$t('delete')" @click="onDeleteClick(user)" />
          </div>
        </ListCard>
      </div>
      <div v-if="total > 10" class="flex justify-center items-center mt-8">
        <IconButton icon="chevron_left" @click="prev" />
        <span class="px-3">{{ pageNumber + 1 }}</span>
        <IconButton icon="chevron_right" @click="next" />
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import dayjs from 'dayjs';
import { apiGetProjectCheckUser } from '../../../../helpers/api';
import AddUserModal from './settings-modals/AddUserModal.vue';
import EditUserModal from './settings-modals/EditUserModal.vue';
import EditUserDataModal from './settings-modals/EditUserDataModal.vue';
import DeleteModal from '../../../modals/DeleteModal.vue';
import debounce from '../../../../helpers/debounce';

export default {
  name: 'Users',
  data() {
    return {
      fetching: false,
      pageNumber: 0,
      total: 0,
      userAction: null,
      email: '',
      emailChecking: false,
      roleChanging: false,
      userName: '',
      userPicture: '',
    };
  },
  computed: {
    ...mapGetters(['getProjectUsers']),
    projectId() {
      return this.$route.params.projectId;
    },
    users() {
      return this.getProjectUsers();
    },
  },
  methods: {
    ...mapActions(['showToastMessage', 'fetchProjectUsers', 'deleteProjectUser', 'updateProjectUserRole', 'disableProjectUser']),
    dayjs,
    async onAddExistingUserClick() {
      this.roleChanging = true;
      const response = await this.updateProjectUserRole({ project_id: this.projectId, email: this.email, role: 'user' });
      if (response) {
        this.showToastMessage({ message: this.$t('projects.iam.added_successfully'), type: 'success' });
        this.$refs.tooltip.hide();
      }
      this.roleChanging = false;
    },
    onUserDropdownShown() {
      this.email = '';
      this.userName = '';
      this.userPicture = '';
      this.userAction = null;
      this.emailChecking = false;
    },
    // eslint-disable-next-line
    onEmailInput: debounce(async function (validate) {
      const valid = await validate();
      if (!valid.valid) return;
      await this.checkUserExist();
    }, 500),
    async checkUserExist() {
      try {
        this.emailChecking = true;
        this.userName = '';
        this.userPicture = '';
        const response = await apiGetProjectCheckUser({ project_id: this.projectId, email: this.email });
        this.emailChecking = false;
        if (response.status === 200) {
          this.userAction = response.data.action;
          if (response.data.action === 'add_user') {
            return true;
          }
          if (response.data.action === 'existing_project_user') {
            return this.$t('users.existing_project_user');
          }
          if (response.data.action === 'existing_user') {
            this.userName = response.data?.name || '';
            this.userPicture = response.data?.picture || '';
            return this.$t('users.existing_user');
          }
          return response.data.action;
        }
      } catch {
        return this.$t('validation.failed');
      }
      return this.$t('validation.failed');
    },
    async next() {
      const response = await this.getUsers({ page_number: this.pageNumber + 1 });
      if (Array.isArray(response) && response.length) {
        this.pageNumber++;
      }
    },
    async prev() {
      if (this.pageNumber === 0) {
        return;
      }
      const response = await this.getUsers({ page_number: this.pageNumber - 1 });
      if (response.length) {
        this.pageNumber--;
      }
    },
    async getUsers(params) {
      this.loading = true;
      const response = await this.fetchProjectUsers({ ...params, project_id: this.projectId });
      if (response) {
        this.total = response.count;
      } else {
        this.total = 0;
      }
      this.loading = false;
      return response;
    },
    onAddUserClick(email) {
      this.$refs.tooltip.hide();
      this.$showModal(AddUserModal, { user_email: email });
    },
    onEditClick(user) {
      this.$showModal(EditUserModal, { email: user.email });
    },
    onDataManagementClick(user) {
      this.$showModal(EditUserDataModal, { email: user.email, name: user.name });
    },
    onDeleteClick(user) {
      this.$showModal(DeleteModal, {
        title: this.$t('projects.iam.confirm_remove_user'),
        subtitle: this.$t('projects.iam.confirm_remove_user_subtitle'),
        onConfirm: async () => {
          await this.deleteProjectUser({ project_id: this.projectId, email: user.email });
        },
      });
    },
    onDisableClick(user) {
      this.$showModal(DeleteModal, {
        title: this.$t('projects.iam.confirm_disable_user'),
        subtitle: this.$t('projects.iam.confirm_disable_user_subtitle'),
        onConfirm: async () => {
          await this.disableProjectUser({ project_id: this.projectId, email: user.email, disabled: true });
        },
      });
    },
    onEnableClick(user) {
      this.$showModal(DeleteModal, {
        title: this.$t('projects.iam.confirm_enable_user'),
        subtitle: this.$t('projects.iam.confirm_enable_user_subtitle'),
        onConfirm: async () => {
          await this.disableProjectUser({ project_id: this.projectId, email: user.email, disabled: false });
        },
      });
    },
  },
  watch: {
    projectId: {
      async handler() {
        this.fetching = true;
        await this.getUsers({ page_number: this.pageNumber + 1 });
        this.fetching = false;
      },
      immediate: true,
    },
  },
};
</script>
